import React, { ForwardedRef, useEffect, useMemo } from 'react';
import CustomShaderMaterial from '../CustomShaderMaterial';
import { Color, Matrix4, Mesh, ShaderMaterial, Texture, Vector2 } from 'three';
import { Dict } from '../../../utils/Dict';
import { useFrame, useThree } from '@react-three/fiber';

type Props = {
  map: Texture,
  width: number,
  height: number,
  align?: boolean,
  target?: Mesh,
  offset?: number[],
  bgColor?: number,
  textColor?: number,
  bgAlpha? :number
  flipSide? :boolean
  scale? :number
};

// disables tone mapping
const LabelMaterial = React.forwardRef(({
  map,
  width,
  height,
  scale = 1,
  offset,
  align,
  target,
  bgColor,
  bgAlpha,
  flipSide,
  textColor
}: Props, ref?: ForwardedRef<ShaderMaterial>) =>
{
  const defines = useMemo(() =>
  {
    const def: Dict<number> = {};
    if (align) def.USE_ALIGN = 1;
    return def;
  }, [ align ]);
  const uniforms = useMemo(() =>
  {
    return {
      map: { value: map },
      size: { value: new Vector2() },
      aspect: { value: 1.0 },
      offset: { value: new Vector2() },
      parentMatrix: { value: new Matrix4() },
      bgColor: { value: new Color() },
      textColor: { value: new Color() },
      bgAlpha: { value: 1.0 },
      scale: { value: 1.0 },
      flipSide: { value: 1.0 },
    };
  }, [ map ]);

  const { viewport } = useThree();

  useEffect(() =>
  {
    uniforms.aspect.value = viewport.width / viewport.height;
  }, [ uniforms, viewport.width, viewport.height ]);

  useEffect(() =>
  {
    uniforms.flipSide.value = flipSide? -1 : 1;
  }, [ uniforms, flipSide ]);

  useEffect(() =>
  {
    uniforms.size.value.set(width * scale, height * scale);
  }, [ width, height, uniforms, scale ]);

  useEffect(() =>
  {
    uniforms.bgColor.value.set(bgColor || 0x000000);
  }, [ bgColor, uniforms ]);

  useEffect(() =>
  {
    uniforms.textColor.value.set(textColor || 0xffffff);
  }, [ textColor, uniforms ]);

  useEffect(() =>
  {
    uniforms.bgAlpha.value = bgAlpha === undefined? 1 : bgAlpha;
  }, [ bgAlpha, uniforms ]);

  useEffect(() =>
  {
    if (offset)
      uniforms.offset.value.set(offset[0] * scale, offset[1] * scale);
    else
      uniforms.offset.value.set(0.8 * scale, -0.4 * scale);
  }, [ offset, uniforms, scale ]);

  useFrame(() => {
    if (target?.parent?.matrixWorld) {
      uniforms.parentMatrix.value = target.parent.matrixWorld;
    }

    // this is *quite* hackish but works to scale the label if its container sphere is scaled
    const ancestor = target?.parent?.parent?.parent?.parent;
    if (ancestor) {
      uniforms.scale.value = ancestor.scale.y;
    }
  });

  return <CustomShaderMaterial ref={ref}
    fragmentID="labelFragmentShader"
    vertexID="labelVertexShader"
    uniforms={uniforms}
    defines={defines}
    attach="material"
    transparent
    depthWrite={false}
    depthTest={false}
  />;
});

LabelMaterial.displayName = 'LabelMaterial';

export default LabelMaterial;
